/**
 * @file 广场
 * @author Yongning 2024-09-10
 */

import React, { useMemo } from "react";
import ReactDOM from 'react-dom';
import styles from "./index.module.scss";
import { CommonHead } from "@/components";
import { IUserLoginInfo } from "@/api/user";
import { namespace, IState } from "@/context";
import { GetServerSidePropsContext } from "next";
import ContentCard from "@/components/ContentCard";
import EmptyThreadTips from "@/components/EmptyThreadTips/mIndex";
import { getDefaultChannelActiveKeys } from "@/hooks/useDevicesChannelTab";
import { getNavsList, ITopLevelNav } from "@/api/nav";
import TabSearchComponent from "@/components/TabSearch";
import useTabSearchHook from "../home/hooks/useTabSearchHook";
import getMyCircle, { ICircle } from "@/api/ channel/myCircle";
import getThreadList, { IThreadLisItem } from "@/api/thread/list";
import Carousel, { AttachType, CarouselItem } from "@/components/Carousel";
import CircleHeader from "@/components/CircleHeader";
import useBodyContainer from '@/hooks/useBodyContainer';


import {
  IHotThread,
  getHotThreads,
  getChannelDetail,
  TopLevelChannels,
  BannerConfigType,
  getSubChannelTree,
  ISubChannelTreeRes,
} from "@/api/home";

interface Error {
  message: string;
  code: string | number;
}

export interface DevicesProps {
  ua: string;
  error?: Error[];
  saltkey: string;
  otList: IHotThread[];
  account?: IUserLoginInfo;
  sources: ISubChannelTreeRes;
  defaultChannelActiveSlug: string;
  defaultChannelActiveSort: string;
  channelList: ISubChannelTreeRes[];
  series: string;
  firstPageArticalList: IState[namespace.device][string]["articalList"][string]["data"];
}

const Devices = (props: DevicesProps) => {
  let channelError = null;
  const container = useBodyContainer();

  const { channelList, firstPageArticalList, sources, series } = props;

  const secondaryChannelList = useMemo(() => {
    return (
      channelList.filter(({ slug }) => slug === series)[0]
        ?.sub_channel_info_dtos || []
    );
  }, [channelList, series]);

  channelError = secondaryChannelList.length < 1;

  const { tabs, moreTabs, tabSortFilterMap } =
    useTabSearchHook(secondaryChannelList);

  // 轮播图数据
  const bannerList: CarouselItem[] = (
    sources?.channel_config?.banner_config || []
  ).map((item) => {
    const { link_url, pc_banner_url, video_play_info } = item;
    const hasVideo = video_play_info?.video_uid;

    return {
      alt: "",
      link: {
        src: link_url,
      },
      type: hasVideo ? AttachType.Video : AttachType.Image,
      src: hasVideo ? video_play_info.definitions[1].src : pc_banner_url,
      ...(hasVideo && { fallbackSrc: pc_banner_url }),
    };
  });
  return (
    <div className={styles.deviceMainContent}>
      {channelError ? (
        <EmptyThreadTips textKey="tips_device_series_errror" />
      ) : (
        <>
          <CommonHead
            title={`${sources?.name} - 大疆社区`}
            keywords={sources?.name}
            description="社区圈子，为不同设备和兴趣的用户提供专属沟通交流空间"
            canonicalLink={`https://bbs.dji.com/pro/devices?series=${series}`}
          />
          {container &&
            ReactDOM.createPortal(
            `${sources?.name} - 大疆社区`,
            container
          )}
          {!!bannerList.length && (
            <div className={styles.bannerContent}>
              <Carousel items={bannerList} width={640} height={120} />
            </div>
          )}
          <CircleHeader
            name={sources.name}
            config={sources.channel_config}
            cover={sources.avatar_image}
          />
          <TabSearchComponent<IThreadLisItem>
            channel={TopLevelChannels.equipment_circle_channel}
            id="tid"
            tabs={tabs}
            pageSize={10}
            moreTabs={moreTabs}
            Component={ContentCard}
            tabSortFilterMap={tabSortFilterMap}
            defaultContent={firstPageArticalList}
            fetchApi={({ tabKey, sortKey, offset = 10, pageSize }) =>
              getThreadList({
                offset,
                limit: pageSize,
                sort_type: sortKey,
                channel_slug: tabKey,
              })
            }
          />
        </>
      )}
    </div>
  );
};

const handlePromiseResult = (
  result: PromiseSettledResult<any>,
  setValue: (value: any) => void,
  errorList: Error[]
) => {
  if (result?.status === "fulfilled") {
    setValue(result.value || []);
  } else {
    errorList.push(result.reason);
  }
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const ua = ctx.req.headers["user-agent"] || "";
  const headers = ctx.req ? ctx.req.headers : {};
  const { series } = ctx.query;

  let error: Error[] = [];
  let circleList: ICircle[] = [];
  let hotList: IHotThread[] = [];
  let navList: ITopLevelNav[] = [];
  let bannerList: BannerConfigType[] = [];
  let channelList: ISubChannelTreeRes[] = [];
  let firstPageArticalList: IThreadLisItem[] = [];
  let defaultValues = {
    defaultChannelActiveSort: "",
    defaultChannelActiveSlug: "",
  };
  let sources: ISubChannelTreeRes | [] = {} as unknown as ISubChannelTreeRes;

  try {
    channelList =
      (
        await getSubChannelTree(
          {
            slug: TopLevelChannels.equipment_circle_channel,
            level_num: 3,
          },
          true,
          headers
        )
      )?.sub_channel_info_dtos || [];

    const seriesChannel =
      channelList.filter(({ slug }) => slug === series) || [];
    const { defaultChannelActiveKeys, defaultChannelActiveSort } =
      getDefaultChannelActiveKeys(seriesChannel[0]?.sub_channel_info_dtos);

    defaultValues = {
      defaultChannelActiveSort,
      defaultChannelActiveSlug:
        defaultChannelActiveKeys?.[defaultChannelActiveKeys.length - 1],
    };

    const [artical, circle, navs, cmsSources, hots] = await Promise.allSettled([
      getThreadList(
        {
          channel_slug: defaultValues?.defaultChannelActiveSlug,
          sort_type: defaultValues.defaultChannelActiveSort,
        },
        true,
        headers
      ),
      getMyCircle(true, headers),
      getNavsList(true),
      getChannelDetail(
        {
          slug: series as string,
        },
        true,
        headers
      ),
      getHotThreads(true),
    ]);

    // 处理请求结果
    handlePromiseResult(
      navs,
      (value: typeof navList) => (navList = value),
      error
    );
    handlePromiseResult(
      hots,
      (value: typeof hotList) => (hotList = value),
      error
    );
    handlePromiseResult(
      cmsSources,
      (value: typeof sources) => (sources = value),
      error
    );
    handlePromiseResult(
      circle,
      (value: typeof circleList) => (circleList = value),
      error
    );
    handlePromiseResult(
      artical,
      (value: typeof firstPageArticalList) => (firstPageArticalList = value),
      error
    );
  } catch (e) {}

  const matchCookies = ctx.req.headers.cookie?.match(/H5VF_2132_saltkey=(\w+)/);

  return {
    props: {
      ua,
      error,
      navList,
      hotList,
      sources,
      circleList,
      bannerList,
      channelList,
      series: series || null,
      firstPageArticalList,
      saltkey: matchCookies && matchCookies.length >= 2 ? matchCookies[1] : "",
      defaultChannelActiveSlug: defaultValues?.defaultChannelActiveSlug || null,
      defaultChannelActiveSort: defaultValues?.defaultChannelActiveSort || null,
    },
  };
}

export default Devices;
